import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader, Loading, ErrorState, Empty, Status, Field } from "../components/Layout";
import { useApi, useResource } from "../lib/api";

export default function Studio() {
  const api = useApi();
  const nav = useNavigate();
  const projects = useResource("/projects");
  const [form, setForm] = useState({ title: "", artist: "", description: "", source_type: "isolated_vocal" });
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Give the project a title");
    setBusy(true);
    try { const p = await api("POST", "/projects", form); nav(`/studio/${p.id}`); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? Its media is removed from your workspace.`)) return;
    try { await api("DELETE", `/projects/${p.id}`); projects.reload(); toast.success("Project deleted"); } catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      <PageHeader title="Studio" sub="New backing arrangements around your original vocal. The supplied vocal is locked: never pitch-shifted, stretched, tuned or regenerated." />
      <div className="grid lg:grid-cols-[380px_1fr] gap-5">
        <form onSubmit={create} className="card p-5 space-y-4 h-fit" data-testid="new-project-form">
          <h2 className="font-semibold">New project</h2>
          <Field label="Title"><input className="input" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title-input" /></Field>
          <Field label="Artist (optional)"><input className="input" value={form.artist} maxLength={120} onChange={(e) => setForm({ ...form, artist: e.target.value })} data-testid="project-artist-input" /></Field>
          <Field label="What are you starting from?">
            <div className="grid gap-2">
              {[["isolated_vocal", "Isolated vocal", "Best: your exact vocal stays untouched."], ["stereo_master", "Stereo master", "We separate an AI-estimated vocal (can contain bleed)."]].map(([v, l, d]) => (
                <label key={v} className={`card-2 p-3 cursor-pointer flex gap-3 ${form.source_type === v ? "border-[var(--pink)]" : ""}`}>
                  <input type="radio" name="src" checked={form.source_type === v} onChange={() => setForm({ ...form, source_type: v })} data-testid={`source-type-${v}`} />
                  <span><span className="block text-sm font-medium">{l}</span><span className="block text-xs muted">{d}</span></span>
                </label>))}
            </div>
          </Field>
          <button className="btn btn-primary w-full justify-center" disabled={busy} data-testid="create-project-button"><Plus size={16} />{busy ? "Creating…" : "Create project"}</button>
        </form>
        <section>
          {projects.loading ? <Loading /> : projects.error ? <ErrorState error={projects.error} onRetry={projects.reload} /> :
            projects.data.length === 0 ? <Empty title="No Studio projects yet">Create your first project to declare rights and upload audio.</Empty> :
              <div className="grid sm:grid-cols-2 gap-4">{projects.data.map((p) => (
                <div key={p.id} className="card p-5 flex flex-col gap-3 fade-up" data-testid={`project-card-${p.id}`}>
                  <div className="flex justify-between gap-2"><Link to={`/studio/${p.id}`} className="font-semibold hover:text-[var(--pink)] truncate">{p.title}</Link><Status value={p.status} /></div>
                  <div className="text-xs muted">{p.source_type === "isolated_vocal" ? "Isolated vocal" : "Stereo master"} · private · updated {new Date(p.updated_at).toLocaleDateString()}</div>
                  {p.quarantined && <span className="badge b-bad">Quarantined: {p.quarantine_reason}</span>}
                  <div className="flex gap-2 mt-auto"><Link to={`/studio/${p.id}`} className="btn btn-ghost btn-sm">Open</Link>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(p)} aria-label="Delete project" data-testid={`delete-project-${p.id}`}><Trash2 size={14} /></button></div>
                </div>))}</div>}
        </section>
      </div>
    </div>
  );
}
